import React from 'react';
import { BookOpen, Clock, MapPin, CheckCircle2, Phone, Star } from 'lucide-react';

const steps = [
  {
    number: '1',
    icon: BookOpen,
    title: 'الاستعداد للعمرة',
    description: 'قبل السفر لأداء العمرة من الرياض، تأكد من حصولك على تأشيرة العمرة من الجهات المختصة. نوفر لك خدمات تسهيل التأشيرات بأسرع وقت وأقل تكلفة عبر فريقنا المتخصص.',
    tips: ['تجهيز جواز السفر الساري', 'الحصول على تأشيرة العمرة', 'التخطيط لمدة الإقامة'],
  },
  {
    number: '2',
    icon: MapPin,
    title: 'اختيار الباقة المناسبة',
    description: 'نقدم باقات عمرة من الرياض تناسب جميع الميزانيات: الباقة الاقتصادية لمن يبحث عن السعر المناسب، وباقة VIP لمن يرغب في رحلة فاخرة، وحجز مقاعد الباص فقط لمن يرغب في المرونة.',
    tips: ['باقات يومية من الرياض', 'باقات VIP أسبوعية', 'مرونة في اختيار الفنادق'],
  },
  {
    number: '3',
    icon: CheckCircle2,
    title: 'الإحرام والذهاب للميقات',
    description: 'عند الانطلاق من الرياض، تتوقف الحافلة عند ميقات أهل الرياض (السيل الكبير) لأداء الإحرام. مرشدنا الديني يرافقك خطوة بخطوة لأداء مناسك العمرة بشكل صحيح.',
    tips: ['الإحرام من السيل الكبير', 'إرشاد ديني متخصص', 'الثياب البيضاء للرجال'],
  },
  {
    number: '4',
    icon: Clock,
    title: 'أداء مناسك العمرة',
    description: 'تشمل مناسك العمرة: الطواف حول الكعبة المشرفة سبع مرات، والسعى بين الصفا والمروة سبع مرات، ثم الحلق أو التقصير. مرشدنا الديني يكون معك طوال الرحلة.',
    tips: ['الطواف 7 مرات', 'السعي 7 مرات', 'الحلق أو التقصير'],
  },
];

export default function UmrahGuide() {
  return (
    <section id="umrah-guide" className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-16">
          <span className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-cairo font-medium mb-4">
            <BookOpen size={16} />
            دليل العمرة الشامل
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-5xl font-amiri font-bold text-gray-900 mb-4 md:mb-6">
            دليل العمرة من الرياض
          </h2>
          <p className="text-gray-600 font-cairo max-w-3xl mx-auto text-sm md:text-lg leading-relaxed">
            دليلك الكامل لأداء العمرة من الرياض — من التجهيزات الأولية إلى أداء المناسك، 
            نشرح لك كل خطوة بالتفصيل لضمان رحلة إيمانية مريحة ومباركة.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-5 md:p-8 border border-gray-100 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <span className="text-white font-cairo font-bold text-base md:text-xl">{step.number}</span>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-cairo font-bold text-gray-900 mb-1 md:mb-2">{step.title}</h3>
                    <p className="text-gray-600 font-cairo leading-relaxed text-sm md:text-base">{step.description}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 md:ml-14">
                  {step.tips.map((tip, tipIndex) => (
                    <span key={tipIndex} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 md:px-3 py-1 rounded-full text-xs font-cairo">
                      <CheckCircle2 size={12} />
                      {tip}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="bg-gradient-to-br from-brown-600 to-brown-800 rounded-3xl p-6 md:p-12 text-white">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
            <div>
              <h3 className="text-xl md:text-2xl lg:text-3xl font-amiri font-bold mb-3 md:mb-4">
                هل تبحث عن رحلة عمرة من الرياض؟
              </h3>
              <p className="text-brown-100 font-cairo leading-relaxed mb-4 md:mb-6 text-sm md:text-base">
                سدرة المنتهى هي شركتك الموثوقة لرحلات العمرة من الرياض إلى مكة المكرمة والمدينة المنورة. 
                نوفر حزم عمرة متكاملة بأسعار منافسة تشمل النقل بالباص الحديث والسكن الفندقي 
                والمرشد الديني المتخصص. اتصل بنا الآن للحجز والاستفسار.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <a
                  href="https://wa.me/966575179602?text=السلام عليكم، أريد الاستفسار عن حجز رحلة عمرة من الرياض"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-brown-700 px-5 md:px-6 py-2.5 md:py-3 rounded-xl font-cairo font-bold text-sm md:text-base flex items-center justify-center gap-2 hover:bg-brown-50 transition-all"
                >
                  <Phone size={18} />
                  احجز عبر واتساب
                </a>
              </div>
            </div>
            <div className="bg-white/10 rounded-2xl p-5 md:p-6 backdrop-blur-sm">
              <h4 className="font-cairo font-bold text-base md:text-lg mb-3 md:mb-4 flex items-center gap-2">
                <Star size={20} className="text-gold-300" />
                لماذا تختار سدرة المنتهى؟
              </h4>
              <ul className="space-y-2 md:space-y-3 font-cairo text-brown-100 text-sm md:text-base">
                <li className="flex items-start gap-2 md:gap-3">
                  <CheckCircle2 size={16} className="text-gold-300 mt-0.5 flex-shrink-0" />
                  <span>رحلات يومية من الرياض إلى مكة والمدينة</span>
                </li>
                <li className="flex items-start gap-2 md:gap-3">
                  <CheckCircle2 size={16} className="text-gold-300 mt-0.5 flex-shrink-0" />
                  <span>باصات حديثة موديل 2027 مكيفة ومريحة</span>
                </li>
                <li className="flex items-start gap-2 md:gap-3">
                  <CheckCircle2 size={16} className="text-gold-300 mt-0.5 flex-shrink-0" />
                  <span>فنادق 3-5 نجوم قريبة من الحرم</span>
                </li>
                <li className="flex items-start gap-2 md:gap-3">
                  <CheckCircle2 size={16} className="text-gold-300 mt-0.5 flex-shrink-0" />
                  <span>أسعار واضحة بدون رسوم خفية</span>
                </li>
                <li className="flex items-start gap-2 md:gap-3">
                  <CheckCircle2 size={16} className="text-gold-300 mt-0.5 flex-shrink-0" />
                  <span>حجز وتأكيد عبر واتساب مباشرة</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
